import { FileUpload } from '@components/form/FileUpload';
import { Stack } from '@mui/material';
import { useField } from 'formik';
import { useCallback } from 'react';
import { FormFieldError } from './FormFieldError';

type FormFileUploadProps = {
  accept?: string;
  label?: string;
  maintainGutters?: boolean;
  multiple?: boolean;
  name: string;
  onFileLoad?: (file: File) => void;
  webkitdirectory?: boolean;
};

export const FormFileUpload = ({
  multiple = false,
  name,
  accept,
  label,
  maintainGutters = true,
  onFileLoad,
  webkitdirectory,
}: FormFileUploadProps) => {
  const [field, meta, helpers] = useField<File | File[] | null>(name);
  const error = meta?.touched && meta?.error;

  const handleFileLoad = useCallback(
    (file: File | File[] | null) => {
      if (multiple) {
        // For multiple files, append to existing array
        const currentFiles = Array.isArray(field.value) ? field.value : [];
        const newFiles = Array.isArray(file) ? file : file ? [file] : [];
        const allFiles = [...currentFiles, ...newFiles];
        void helpers.setValue(allFiles);
      } else {
        // For single file, just set the value
        void helpers.setValue(file);
        if (file && !Array.isArray(file)) {
          onFileLoad?.(file);
        }
      }
    },
    [multiple, field.value, helpers, onFileLoad],
  );

  const handleRemove = useCallback(
    (index: number) => {
      if (multiple && Array.isArray(field.value)) {
        const updatedFiles = [...field.value.slice(0, index), ...field.value.slice(index + 1)];
        void helpers.setValue(updatedFiles);
      } else {
        void helpers.setValue(null);
      }
    },
    [multiple, field.value, helpers],
  );

  return (
    <Stack>
      <FileUpload
        accept={accept}
        name={name}
        label={label}
        multiple={multiple}
        value={field.value}
        onFileLoad={handleFileLoad}
        onRemove={handleRemove}
        webkitdirectory={webkitdirectory}
      />
      <FormFieldError error={error} maintainGutters={maintainGutters} />
    </Stack>
  );
};
